import { ChangeEventHandler, FormEvent, useEffect, useRef, useState } from 'react';
import type { FC } from 'react';
import { Box, Button, FormHelperText, TextField as MuiTextField,
  useMediaQuery} from '@mui/material';
import { useSimpleSaveAction } from '@/controller/SaveContextProvider';
import { SimpleFormContextProvider, SrRegion, Status, useLoginContext, useSimpleForm } from '@/pages/authentication/LoginContext';
import { useSaveContext } from 'ra-core';
import retrieveAxiosErr from '@/utils/retrieveAxiosErr';



const validators = {
  code: (x:string) => {
    if (!x || x === '' || x.length < 6) { return 'Code is required'}
    if (!/^\d{6}$/.test(x)) { return 'Code should be 6 digits' }
  },
}

const VerifyCodeForm: FC = () => {
  const verifyCode = useSimpleSaveAction()
  const { saving: isSubmitting } = useSaveContext()
  const { dispatch, states, deviceFlow } = useLoginContext()
  
  const smallScreen = useMediaQuery('(max-width: 530px)')
  const itemsRef = useRef<HTMLInputElement[]>([]);

  useEffect(() => {
    itemsRef.current = itemsRef.current.slice(0, 6);
  }, []);

  const [localStates, setLocalStates] = useState({
    codeChars: ['', '', '', '', '', ''],
    touched: false,
  })
  
  const ctrl = useSimpleForm(validators)
  const { formStates, forceValidate } = ctrl

  const handleClick = (event:FormEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (!states.phase1Result || states.status !== Status.Phase2) {
      dispatch({ type: 'p1_setback' })
      return
    }
    const code = localStates.codeChars.join('')
    const formStates = forceValidate({ code })
    if (formStates.errors['code']) { 
      setLocalStates(v => ({...v, touched: true}))
      return 
    }

    dispatch({ type: 'error_clear' })
    verifyCode(code as any, {
      onFailure: (err:any) => {
        const errMsg = retrieveAxiosErr(err)
        dispatch({ type: 'error_occur', error: errMsg })
      }
    })
  }

  const compoundedInput: ChangeEventHandler<HTMLInputElement> = (e) => {
    const valueStr = e.target.value || ''
    const chars = [...(valueStr.slice(0, 6).replace(/[^\d]/g, ''))]
    const touchedCount = chars.length
    chars.length = 6
    const codes = [...chars].map(c => Number.isInteger(parseInt(c, 10)) ? c : '')
    setLocalStates(v => {
      const v1 = {codeChars: codes, touched: v.touched}
      if (!v1.touched && touchedCount === 6) {
        v1.touched = true
      }
      if (v1.touched) {
        setTimeout(() => forceValidate({ code: v1.codeChars.join('')}))
      }
      return v1
    })
  }
  
  const formRef = useRef<HTMLFormElement|null>(null)
  useEffect(() => {
    setTimeout(() => {
      const fstElem : HTMLInputElement|null = formRef.current?.elements[0] as any
      fstElem?.focus()
    }, 200)
  }, [])

  return (
    <SimpleFormContextProvider value={ctrl}>
      <SrRegion aria-labelledby="sign-in-title" boxRef={null}>
        {smallScreen ?
          <Box>
            <MuiTextField 
              id="code-compounded"
              autoComplete='off'
              error={Boolean(
                localStates.touched
                && formStates.errors['code']
              )}
              onInput={compoundedInput}
              fullWidth
              value={localStates.codeChars.join('')}
              sx={{
                display: 'inline-block',
                textAlign: 'center',
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                  letterSpacing: '32px',
                }
              }}
              variant="outlined"
              inputProps={{ form: 'login-form' }}
            />
          </Box> :
          <Box
            sx={{
              display: 'grid',
              columnGap: '16px',
              gridTemplateColumns: 'repeat(7, 1fr)',
              pt: 1,
            }}
          >
            {[1, 2, 3, 4, 5, 6].map((ref, i) => (
              <MuiTextField
                id={`code-${i}`}
                autoComplete='off'
                error={Boolean(
                  localStates.touched
                  && formStates.errors['code']
                )}
                fullWidth
                inputRef={(el) => itemsRef.current[i] = el}
                inputProps={{ form: 'login-form' }}
                 
                key={`code-${i}`}
                name={`code[${i}]`}
                onKeyDown={(event) => {
                  if (event.code.toUpperCase() === 'BACKSPACE') {
                    if (localStates.codeChars[i]) {
                      setLocalStates(v => {
                        v.codeChars[i] = ''
                        return {...v}
                      })
                      return;
                    }
                    
                    if (i > 0) {
                      setLocalStates(v => {
                        v.codeChars[i] = ''
                        return {...v}
                      })
                      itemsRef.current[i - 1].focus();
                      return;
                    }
                  }
                  
                  if (Number.isInteger(parseInt(event.key, 10))) {
                    setLocalStates(v => {
                      const v1 = {...v}
                      v.codeChars[i] = event.key
                      if (!v1.touched && v1.codeChars.every(c => !!c)) {
                        v1.touched = true
                      }
                      setTimeout(() => forceValidate({ code: v1.codeChars.join('')}))
                      return v1
                    })

                    if (i < 5) {
                      itemsRef.current[i + 1].focus();
                    }
                  }
                }}
                onPaste={(event) => {
                  const paste = event.clipboardData.getData('text');
                  const pasteArray = paste.split('');

                  if (pasteArray.length !== 6) {
                    return;
                  }

                  const valid = pasteArray.every((x) => Number.isInteger(parseInt(x, 10)))
                  if (valid) {
                    setLocalStates({ codeChars: pasteArray, touched: true })
                    setTimeout(() => itemsRef.current[5].focus());
                  }
                }}
                value={localStates.codeChars[i]}
                sx={{
                  display: 'inline-block',
                  textAlign: 'center',
                  '& .MuiInputBase-input': {
                    textAlign: 'center'
                  }
                }}
                variant="outlined"
              />
            ))}
          </Box>
        }
        {
          Boolean(
            localStates.touched
            && formStates.errors['code']
          ) && (
            <FormHelperText
              error
              sx={{ mx: '14px' }}
            >
              {formStates.errors['code']}
            </FormHelperText>
          )
        }
        {states.errMsg && (
          <Box sx={{ mt: 3 }}>
            <FormHelperText error>
              {states.errMsg}
            </FormHelperText>
          </Box>
        )}
        <Box sx={{ mt: 3 }}>
          <form id="login-form" onSubmit={handleClick} ref={formRef}>
            <Button
              color="primary"
              disabled={isSubmitting}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
            >
              Verify
            </Button>
          </form>
        </Box>
      </SrRegion>
    </SimpleFormContextProvider>
  );
};

export default VerifyCodeForm;
